import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import styles from './NoteStudy.module.css';
import { LoaderIcon, HeartIcon } from '../../components/Icons';
import LoadingSpinner from '../../components/LoadingSpinner';

/* ────────────────────────────────
   helpers
   ──────────────────────────────── */

const normalizeNotes = (payload) => {
  if (!Array.isArray(payload)) return [];
  return payload.map((note) => ({
    id: note?.id ?? note?.note_id ?? Math.random().toString(36).slice(2),
    note_content: note?.note_content ?? note?.content ?? '',
    created_at: note?.created_at ?? note?.createdAt ?? null,
    chapter_id: note?.chapter_id ?? note?.chapterId ?? null,
    chapter_title: note?.chapter_title ?? note?.chapterTitle ?? '',
    sentence_id: note?.sentence_id ?? note?.sentenceId ?? null,
    novel_id: note?.novel_id ?? note?.novelId ?? null,
    volume_id: note?.volume_id ?? note?.volumeId ?? null,
  }));
};

/** 将 **bold** 渲染为 <strong>，换行渲染为 <br/> */
const formatRichContent = (text) => {
  if (!text) return null;
  // 先按换行拆分
  const lines = text.split(/\r?\n/);
  const elements = [];
  lines.forEach((line, lineIdx) => {
    if (lineIdx > 0) elements.push(<br key={`br-${lineIdx}`} />);
    // 处理 **bold**
    const segments = line.split(/(\*\*[\s\S]*?\*\*)/g).filter(Boolean);
    segments.forEach((seg, segIdx) => {
      if (seg.startsWith('**') && seg.endsWith('**')) {
        elements.push(
          <strong key={`b-${lineIdx}-${segIdx}`} className={styles.richBold}>
            {seg.slice(2, -2)}
          </strong>
        );
      } else {
        elements.push(<React.Fragment key={`t-${lineIdx}-${segIdx}`}>{seg}</React.Fragment>);
      }
    });
  });
  return elements;
};

const parseSentences = (raw) => {
  try {
    const parsed = JSON.parse(raw || '[]');
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => item?.sentence_content || item?.content || '')
        .filter((t) => typeof t === 'string' && t.trim().length > 0);
    }
    if (parsed?.sentences && Array.isArray(parsed.sentences)) {
      return parsed.sentences
        .map((item) => item?.sentence_content || item?.content || '')
        .filter((t) => typeof t === 'string' && t.trim().length > 0);
    }
  } catch (_) {
    return (raw || '').split(/\n+/).filter((t) => t.trim());
  }
  return [];
};

/* ────────────────────────────────
   PhraseStudyCard — 单个短语的学习卡片
   ──────────────────────────────── */

const PhraseStudyCard = ({ phrase, globalMeaning, globalUsage, globalExample }) => {
  // 局部状态：点击 reveal 按钮控制当前短语
  const [localMeaning, setLocalMeaning] = useState(false);
  const [localUsage, setLocalUsage] = useState(false);
  const [localExample, setLocalExample] = useState(false);

  // 切换短语时重置局部状态
  useEffect(() => {
    setLocalMeaning(false);
    setLocalUsage(false);
    setLocalExample(false);
  }, [phrase]);

  if (!phrase || typeof phrase !== 'object') return null;

  // 总控 ON → 直接显示；总控 OFF → 由局部状态决定
  const meaningVisible = globalMeaning || localMeaning;
  const usageVisible = globalUsage || localUsage;
  const exampleVisible = globalExample || localExample;

  return (
    <div className={styles.phraseCard}>
      {/* 短语标题 — 始终显示 */}
      <div className={styles.phraseHeader}>
        <h3 className={styles.phraseTitle}>{phrase.phrase || '短语'}</h3>
      </div>

      {/* 释义 */}
      {phrase.meaning && (
        <button
          type="button"
          className={`${styles.revealBtn} ${meaningVisible ? styles.revealBtnActive : ''}`}
          onClick={(e) => { e.stopPropagation(); setLocalMeaning((v) => !v); }}
        >
          <span className={styles.revealLabel}>
            <span className="material-icons-round" style={{ fontSize: '18px' }}>translate</span>
            释义
          </span>
          {meaningVisible ? (
            <span className={styles.revealContent}>{phrase.meaning}</span>
          ) : (
            <span className={styles.revealHint}>点击查看</span>
          )}
        </button>
      )}

      {/* 用法 */}
      {phrase.usage_note && (
        <button
          type="button"
          className={`${styles.revealBtn} ${usageVisible ? styles.revealBtnActive : ''}`}
          onClick={(e) => { e.stopPropagation(); setLocalUsage((v) => !v); }}
        >
          <span className={styles.revealLabel}>
            <span className="material-icons-round" style={{ fontSize: '18px' }}>menu_book</span>
            用法
          </span>
          {usageVisible ? (
            <span className={styles.revealContent}>{phrase.usage_note}</span>
          ) : (
            <span className={styles.revealHint}>点击查看</span>
          )}
        </button>
      )}

      {/* 例句 */}
      {(phrase.example_en || phrase.example_cn) && (
        <button
          type="button"
          className={`${styles.revealBtn} ${exampleVisible ? styles.revealBtnActive : ''}`}
          onClick={(e) => { e.stopPropagation(); setLocalExample((v) => !v); }}
        >
          <span className={styles.revealLabel}>
            <span className="material-icons-round" style={{ fontSize: '18px' }}>format_quote</span>
            例句
          </span>
          {exampleVisible ? (
            <div className={styles.exampleBox}>
              {phrase.example_en && (
                <p className={styles.exampleEn}>{phrase.example_en}</p>
              )}
              {phrase.example_cn && (
                <p className={styles.exampleCn}>{phrase.example_cn}</p>
              )}
            </div>
          ) : (
            <span className={styles.revealHint}>点击查看</span>
          )}
        </button>
      )}
    </div>
  );
};

/* ────────────────────────────────
   NoteStudy — 主页面
   ──────────────────────────────── */

const NoteStudy = () => {
  const { bookId: paramBookId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const bookId = paramBookId || searchParams.get('bookid') || searchParams.get('bookId') || null;
  const chapterId = searchParams.get('chapterid') || searchParams.get('chapterId') || null;

  const initialStateRef = useRef(location.state);
  const chapterCacheRef = useRef(new Map());

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [chapterDataMap, setChapterDataMap] = useState({});

  // 全局显示开关
  const [showSentence, setShowSentence] = useState(false);
  const [showMeaning, setShowMeaning] = useState(false);
  const [showUsage, setShowUsage] = useState(false);
  const [showExample, setShowExample] = useState(false);

  useEffect(() => {
    if (location.state) initialStateRef.current = location.state;
  }, [location.state]);

  // Reset phrase index when note changes (toggles persist across all notes)
  useEffect(() => {
    setPhraseIndex(0);
  }, [currentIndex]);

  /* 加载笔记 */
  useEffect(() => {
    const stateNotes = location.state?.notes;
    if (Array.isArray(stateNotes) && stateNotes.length) {
      setNotes(normalizeNotes(stateNotes));
      setLoading(false);
      setError('');
      return;
    }

    if (!bookId && !chapterId) {
      setLoading(false);
      setNotes([]);
      setError('未找到对应的书籍或章节');
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        if (bookId) params.novel_id = bookId;
        if (chapterId) params.chapter_id = chapterId;
        const response = await api.get('/user-notes/', { params });
        if (cancelled) return;
        const normalized = normalizeNotes(response.data);
        setNotes(normalized);
        if (!normalized.length) setError('暂无学习笔记');
      } catch (err) {
        console.error('加载笔记失败:', err);
        if (!cancelled) {
          setError('加载笔记失败，请稍后再试');
          setNotes([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [bookId, chapterId, location.state]);

  /* 加载章节内容（content + ai_notes） */
  const pendingChapterIds = useMemo(() => {
    const ids = new Set();
    notes.forEach((n) => {
      if (n.chapter_id != null) {
        const k = String(n.chapter_id);
        if (!chapterDataMap[k]) ids.add(k);
      }
    });
    return Array.from(ids);
  }, [notes, chapterDataMap]);

  useEffect(() => {
    if (!pendingChapterIds.length) return;
    let cancelled = false;
    const load = async () => {
      const results = await Promise.allSettled(
        pendingChapterIds.map(async (chId) => {
          if (chapterCacheRef.current.has(chId)) return { chId, data: chapterCacheRef.current.get(chId) };
          const res = await api.get(`/chapters/${chId}`);
          const ch = res.data;
          const contentSentences = parseSentences(ch.content);
          const translationSentences = parseSentences(ch.content_trans);
          let aiNotesData = [];
          if (ch.ai_notes) {
            try {
              const p = typeof ch.ai_notes === 'string' ? JSON.parse(ch.ai_notes) : ch.ai_notes;
              if (Array.isArray(p)) aiNotesData = p;
            } catch (_) { /* ignore */ }
          }
          const data = { contentSentences, translationSentences, aiNotesData };
          chapterCacheRef.current.set(chId, data);
          return { chId, data };
        })
      );
      if (cancelled) return;
      const entries = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled') entries[r.value.chId] = r.value.data;
      });
      if (Object.keys(entries).length) {
        setChapterDataMap((prev) => ({ ...prev, ...entries }));
      }
    };
    load();
    return () => { cancelled = true; };
  }, [pendingChapterIds]);

  /* 构建学习条目列表
     - AI 笔记 (note_content === '-1')：按句子分组，保留 PhraseStudyCard
     - 其他笔记（NoteModal AI 回复、手写笔记）：每条独立成卡片
  */
  const studyItems = useMemo(() => {
    if (!notes.length) return [];

    const aiGroups = new Map();   // AI 笔记按句子分组
    const otherItems = [];        // 非 AI 笔记各自独立

    notes.forEach((note) => {
      const chId = note.chapter_id != null ? String(note.chapter_id) : null;
      const sentenceRaw = note.sentence_id;
      let numericSid = null;
      if (typeof sentenceRaw === 'number' && Number.isFinite(sentenceRaw)) numericSid = sentenceRaw;
      else if (typeof sentenceRaw === 'string') {
        const p = Number(sentenceRaw);
        if (Number.isFinite(p)) numericSid = p;
      }

      const chData = chId ? chapterDataMap[chId] : null;
      const contentSentences = chData?.contentSentences || [];
      const translationSentences = chData?.translationSentences || [];
      const lookupIdx = Number.isFinite(numericSid) && numericSid > 0 ? numericSid - 1 : numericSid;
      const originalText = Number.isFinite(lookupIdx) && lookupIdx >= 0 ? contentSentences[lookupIdx] || '' : '';
      const translatedText = Number.isFinite(lookupIdx) && lookupIdx >= 0 ? translationSentences[lookupIdx] || '' : '';

      const isAiNote = note.note_content === '-1';

      if (isAiNote) {
        // AI 笔记按句子分组
        const groupKey = `ai-${chId ?? 'ch'}-${numericSid ?? 'u'}`;
        let g = aiGroups.get(groupKey);
        if (!g) {
          const aiNotesData = chData?.aiNotesData || [];
          let phrases = [];
          if (numericSid != null) {
            const aiNote = aiNotesData.find((item) => Number(item?.sentence_id ?? item?.sentenceId) === numericSid);
            if (aiNote) {
              phrases = Array.isArray(aiNote.key_phrases) ? aiNote.key_phrases
                : Array.isArray(aiNote.keyPhrases) ? aiNote.keyPhrases : [];
            }
          }
          g = {
            key: groupKey,
            chapterId: chId,
            sentenceId: numericSid,
            originalText,
            translatedText,
            isAiNote: true,
            phrases,
            noteContent: '',
            noteIds: [],
          };
          aiGroups.set(groupKey, g);
        }
        g.noteIds.push(note.id);
      } else {
        // 非 AI 笔记：每条独立
        otherItems.push({
          key: `note-${note.id}`,
          chapterId: chId,
          sentenceId: numericSid,
          originalText,
          translatedText,
          isAiNote: false,
          phrases: [],
          noteContent: note.note_content || '',
          noteIds: [note.id],
          createdAt: note.created_at,
        });
      }
    });

    // AI 笔记在前，非 AI 笔记在后（按创建时间倒序）
    const aiItems = Array.from(aiGroups.values());
    otherItems.sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });
    return [...aiItems, ...otherItems];
  }, [notes, chapterDataMap]);

  const currentItem = studyItems[currentIndex] || null;
  const totalPhrases = currentItem?.isAiNote ? (currentItem.phrases?.length || 0) : 0;
  const isLastPhrase = totalPhrases <= 1 || phraseIndex >= totalPhrases - 1;
  const isFirstPhrase = phraseIndex <= 0;

  /* ── 统一导航：下一个 ── */
  const handleNext = useCallback(() => {
    if (!studyItems.length) return;
    // AI 笔记且还有下一个 phrase
    if (currentItem?.isAiNote && totalPhrases > 1 && !isLastPhrase) {
      setPhraseIndex((i) => i + 1);
      return;
    }
    // 跳到下一条笔记
    if (currentIndex < studyItems.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      // 已是最后一条，回到第一条
      setCurrentIndex(0);
    }
  }, [studyItems, currentItem, totalPhrases, isLastPhrase, currentIndex]);

  /* ── 统一导航：上一个 ── */
  const handlePrev = useCallback(() => {
    if (!studyItems.length) return;
    // AI 笔记且还有上一个 phrase
    if (currentItem?.isAiNote && totalPhrases > 1 && !isFirstPhrase) {
      setPhraseIndex((i) => i - 1);
      return;
    }
    // 跳到上一条笔记
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    } else {
      // 已是第一条，回到最后一条
      setCurrentIndex(studyItems.length - 1);
    }
  }, [studyItems, currentItem, totalPhrases, isFirstPhrase, currentIndex]);

  /* 返回 */
  const handleBack = useCallback(() => {
    const s = initialStateRef.current;
    if (s?.from === 'historyNotes') { navigate('/study/notes'); return; }
    if (s?.returnPath) {
      if (typeof s.returnPath === 'string') { navigate(s.returnPath); return; }
      if (s.returnPath.pathname) {
        navigate(`${s.returnPath.pathname}${s.returnPath.search || ''}`, { state: s.returnPath.state });
        return;
      }
    }
    if (s?.from) {
      navigate(s.from, { state: s.historyReturn ? { historyReturn: s.historyReturn } : undefined });
      return;
    }
    if (chapterId && bookId) {
      navigate(`/chapterlearn?chapterid=${chapterId}&bookid=${bookId}&tab=notes`);
      return;
    }
    if (bookId) { navigate(`/book?bookid=${bookId}`); return; }
    navigate(-1);
  }, [navigate, bookId, chapterId]);

  const handleUnfavorite = async (item) => {
    if (!item?.noteIds?.length) return;
    setFavoriteLoading(true);
    try {
      await Promise.all(item.noteIds.map((id) => api.delete(`/user-notes/${id}`)));
      const removedSet = new Set(item.noteIds);
      const updated = notes.filter((n) => !removedSet.has(n.id));
      setNotes(updated);
      if (!updated.length) { handleBack(); return; }
      setCurrentIndex((i) => Math.min(i, updated.length - 1));
    } catch (err) {
      console.error('取消收藏失败:', err);
      alert('取消收藏失败，请稍后重试');
    } finally {
      setFavoriteLoading(false);
    }
  };

  /* keyboard */
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); handlePrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); handleNext(); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handlePrev, handleNext]);

  /* ── render ── */

  if (loading) {
    return (
      <div className={styles.container}>
        <LoadingSpinner fullPage text="加载笔记中..." />
      </div>
    );
  }

  if (error || !studyItems.length) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <button type="button" className={styles.backBtn} onClick={handleBack}><span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span></button>
          <p>{error || '暂无学习笔记'}</p>
        </div>
      </div>
    );
  }

  // 底部按钮文案：区分 phrase 内切换 vs 笔记切换
  const nextLabel = (currentItem?.isAiNote && totalPhrases > 1 && !isLastPhrase)
    ? '下一个短语'
    : '下一条笔记';
  const prevLabel = (currentItem?.isAiNote && totalPhrases > 1 && !isFirstPhrase)
    ? '上一个短语'
    : '上一条笔记';

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button type="button" className={styles.backBtn} onClick={handleBack}>
          <span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span>
        </button>
        <div className={styles.headerCenter}>
          {currentItem?.isAiNote && totalPhrases > 1 && (
            <span className={styles.phraseDots}>
              {currentItem.phrases.map((_, i) => (
                <span
                  key={i}
                  className={`${styles.phraseDot} ${i === phraseIndex ? styles.phraseDotActive : ''} ${i < phraseIndex ? styles.phraseDotDone : ''}`}
                />
              ))}
            </span>
          )}
          <span className={styles.progress}>
            {currentIndex + 1} / {studyItems.length}
          </span>
        </div>
        <div className={styles.headerRight} />
      </div>

      {/* 笔记级进度条 */}
      <div className={styles.progressBar}>
        <div
          className={styles.progressBarFill}
          style={{ width: `${((currentIndex + 1) / studyItems.length) * 100}%` }}
        />
      </div>

      {/* 控制开关栏 */}
      <div className={styles.toggleBar}>
        <button
          type="button"
          className={`${styles.toggleChip} ${showSentence ? styles.toggleChipActive : ''}`}
          onClick={() => setShowSentence((v) => !v)}
        >
          <span className="material-icons-round" style={{ fontSize: '16px' }}>
            {showSentence ? 'visibility' : 'visibility_off'}
          </span>
          原文
        </button>
        {currentItem?.isAiNote && (
          <>
            <button
              type="button"
              className={`${styles.toggleChip} ${showMeaning ? styles.toggleChipActive : ''}`}
              onClick={() => setShowMeaning((v) => !v)}
            >
              <span className="material-icons-round" style={{ fontSize: '16px' }}>
                {showMeaning ? 'visibility' : 'visibility_off'}
              </span>
              释义
            </button>
            <button
              type="button"
              className={`${styles.toggleChip} ${showUsage ? styles.toggleChipActive : ''}`}
              onClick={() => setShowUsage((v) => !v)}
            >
              <span className="material-icons-round" style={{ fontSize: '16px' }}>
                {showUsage ? 'visibility' : 'visibility_off'}
              </span>
              用法
            </button>
            <button
              type="button"
              className={`${styles.toggleChip} ${showExample ? styles.toggleChipActive : ''}`}
              onClick={() => setShowExample((v) => !v)}
            >
              <span className="material-icons-round" style={{ fontSize: '16px' }}>
                {showExample ? 'visibility' : 'visibility_off'}
              </span>
              例句
            </button>
          </>
        )}
        <button
          type="button"
          className={styles.unfavoriteBtn}
          onClick={() => handleUnfavorite(currentItem)}
          disabled={favoriteLoading}
          title="取消收藏"
        >
          {favoriteLoading ? <LoaderIcon size={18} /> : <HeartIcon size={22} filled />}
        </button>
      </div>

      {/* 学习区域 */}
      <div className={styles.studyBody}>
        <div className={styles.studyItemCard}>
          {/* 句子标签 */}
          <span className={styles.sentenceLabel}>SENTENCE {currentItem?.sentenceId ?? '—'}</span>

          {/* 原文展示 — 受开关控制 */}
          {showSentence && (currentItem?.originalText || currentItem?.translatedText) && (
            <div className={styles.sentenceReveal}>
              {currentItem?.originalText && <div className={styles.sentenceZh}>{currentItem.originalText}</div>}
              {currentItem?.translatedText && <div className={styles.sentenceEn}>{currentItem.translatedText}</div>}
            </div>
          )}

          {/* AI 笔记 — 保留原有 PhraseStudyCard 设计 */}
          {currentItem?.isAiNote && currentItem.phrases.length > 0 ? (
            <div className={styles.phraseStudyArea}>
              <PhraseStudyCard
                phrase={currentItem.phrases[phraseIndex]}
                globalMeaning={showMeaning}
                globalUsage={showUsage}
                globalExample={showExample}
              />
            </div>
          ) : !currentItem?.isAiNote && currentItem?.noteContent ? (
            /* NoteModal AI 回复 & 手写笔记 — 直接显示完整内容 */
            <div className={styles.richNoteCard}>
              <div className={styles.richNoteContent}>
                {formatRichContent(currentItem.noteContent)}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* 底部导航 — 唯一一套 */}
      <div className={styles.footerNav}>
        <button type="button" className={styles.navBtn} onClick={handlePrev} disabled={!studyItems.length}>
          <span className="material-icons-round" style={{ fontSize: '20px' }}>chevron_left</span>
          {prevLabel}
        </button>
        <button type="button" className={styles.navBtn} onClick={handleNext} disabled={!studyItems.length}>
          {nextLabel}
          <span className="material-icons-round" style={{ fontSize: '20px' }}>chevron_right</span>
        </button>
      </div>
    </div>
  );
};

export default NoteStudy;
